import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { EmailBehaviourReportProps } from './EmailBehaviourReport.props';
import { useAppSelector } from '../../redux/hooks';
import {
  EmailBehaviourRow,
  getEmailBehaviourReportEvent,
  managerTeamDataSelector,
} from '../../redux/managerTeamSlice';
import { DataTables } from '../../components/DataTables';
import { GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';
import {
  Box,
  Stack,
  styled,
  Typography,
  TypographyProps,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { UserAvatarLetters } from '../../components/UserAvatarLetters';
import { CustomPagination } from '../../components/DataTables/components/CustomPagination';
import { LoadingPage } from '../../components/LoadingPage';
import { DownloadButton } from '../../components/DownloadButton';
import { useDownloadReport } from '../../components/DownloadButton/useDownloadReport';
import { downloadEmailBehaviourReport } from '../../api/platform';
import './EmailBehaviourReport.css';
import {SearchMenuBar} from "../../components/SearchMenuBar";
import { getDownloadTimestamp } from '../../utils/util';
import { SnackBar } from '../../components/SnackBar';

interface EmailBehaviourReportRow extends EmailBehaviourRow {
  id: number;
}

const defaultSortModel: GridSortModel = [{ field: 'first_name', sort: 'asc' }];

export const EmailBehaviourReport: React.FC<EmailBehaviourReportProps> = () => {
  const [filterSearchKeyword, setFilterSearchKeyword] = useState('');
  const [rows, setRows] = useState<EmailBehaviourReportRow[]>([]);
  const [totalPage, setTotalPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [startPage, setStartPage] = useState(0);
  const [endPage, setEndPage] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const dispatch = useDispatch<any>();

  const { isEmailBehaviourLoading, emailBehaviourData } = useAppSelector(managerTeamDataSelector);

  const theme = useTheme();
  const isLaptop = useMediaQuery(theme.breakpoints.between('tablet', 'laptop'));
  const filename = () => `email-behaviour-${getDownloadTimestamp()}.csv`;

  const defaultParams = {
    per_pages: 10, 
    page: 1,
    keyword: '',
    sort_type: 'asc',
    sort_column: 'first_name',
  };
  const [mainParams, setMainParams] = useState<any>(defaultParams);

  const fetchData = async (params: any) => {
    setMainParams(params);
    await dispatch(getEmailBehaviourReportEvent(params));
  };

  useEffect(() => {
    fetchData(defaultParams);
  }, [dispatch]);

  useEffect(() => {
    const data: EmailBehaviourReportRow[] = emailBehaviourData.list.map((result, index) => ({
      ...result,
      id: index
    }));
    setRows(data);
    setTotalPage(emailBehaviourData['total_page']);
    setTotalRows(emailBehaviourData['total_items']);
    setStartPage(emailBehaviourData['start_item']);
    setEndPage(emailBehaviourData['end_item']);
  }, [emailBehaviourData]);

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    const model = newSortModel?.length > 0 ? newSortModel : defaultSortModel;
    const params = {
      ...mainParams,
      sort_type: model[0].sort,
      sort_column: model[0].field
    };
    fetchData(params);
  };

  const onSearchByKeyword = (event: any) => {
    if (event.keyCode === 13) {
      const params = {
        ...mainParams,
        keyword: event.target.value,
        page: 1
      };
      setCurrentPage(1);
      fetchData(params);
    }
  };

  const onRowsPerPageChange = (perPage: number) => {
    const params = {
      ...mainParams,
      per_pages: perPage,
      page: 1
    };
    setPerPage(perPage);
    setCurrentPage(1);
    fetchData(params);
  };

  const onChangePage = (currentPage: number) => {
    const params = {
      ...mainParams,
      page: currentPage,
    };
    setCurrentPage(currentPage);
    fetchData(params);
  };

  const {
    handleDownload,
    isDownloading,
    label: downloadLabel,
    showSnackbar,
    setShowSnackbar,
    snackbarMessage,
    snackbarSeverity
  } = useDownloadReport(
    () => downloadEmailBehaviourReport({
      keyword: mainParams.keyword,
      sort_column: mainParams.sort_column,
      sort_type: mainParams.sort_type,
    }),
    [
        { key: 'first_name',        label: 'First Name' },
        { key: 'last_name',         label: 'Last Name' },
        { key: 'email',             label: 'Email' },
        { key: 'email_sent',        label: 'Email Sent' },
        { key: 'emails_clicked',    label: 'Emails Clicked' },
        { key: 'emails_reported',   label: 'Emails Reported' },
        { key: 'click_through_rate',label: 'Click Through Rate (%)' },
    ],
    filename
  );

  const columns: GridColDef[] = [
    {
      field: 'first_name',
      headerName: 'Learners',
      flex: isLaptop ? 1.5 : 2,
      sortable: false,
      headerClassName: 'email-behaviour-report-header-start',
      renderCell: (params: GridRenderCellParams) => {
        const firstName = params.row.first_name ? params.row.first_name.trim() : '';
        const lastName = params.row.last_name ? params.row.last_name.trim() : '';
        const learnerName = firstName + ' ' + lastName;
        return (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.875}
            className={'email-behaviour-report-cell'}
          >
            <UserAvatarLetters name={learnerName} classNames={'email-behaviour-report-user-name'} />
            <Stack>
              <CellText>{learnerName}</CellText>
              <CellText color="secondary.main" fontWeight={700} style={{lineBreak: 'anywhere'}}>
                <a className={'email-behaviour-report-cell-email'} href={`mailto:${params.row.email}`}>
                  {params.row.email}
                </a>
              </CellText>
            </Stack>
          </Stack>
        );
      }
    },
    {
      field: 'email_sent',
      headerName: 'Email Sent',
      flex: 1,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        return <CellText>{params.row.email_sent}</CellText>;
      }
    },
    {
      field: 'emails_clicked',
      headerName: 'Emails Clicked',
      flex: 1,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        return <CellText>{params.row.emails_clicked}</CellText>;
      }
    },
    {
      field: 'emails_reported',
      headerName: 'Emails Reported',
      flex: 1,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        return <CellText >{params.row.emails_reported}</CellText>;
      }
    },
    {
      field: 'click_through_rate',
      headerName: 'Click Through Rate',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        return <CellText textAlign={'right'}>{params.row.click_through_rate}%</CellText>;
      }
    }
  ];

  return (
    <Stack spacing={0} className="email-behaviour-report-container">
      <SearchMenuBar
          selectedItems={[]}
          handleEnterSearch={onSearchByKeyword}
          searchInputValue={filterSearchKeyword}
          setSearchInputValue={setFilterSearchKeyword}
          isShowFilter={false}
      />
      <Box className="manager-xp-additional-function">
        <DownloadButton
          onDownload={handleDownload}
          loading={isDownloading}
          disabled={isDownloading}
          label={downloadLabel}
          className="manager-xp-download-button"
        />
      </Box>
      <DataTables
        tblRows={rows}
        tblColumns={columns}
        totalRows={totalRows}
        sortingMode={'server'}
        sortModelChangeHandler={handleSortModelChange}
        customPaging={
          <CustomPagination
            rowsPerPageOptions={[5, 10, 20]}
            currentPage={currentPage}
            totalPage={totalPage}
            perPage={perPage}
            startPage={startPage}
            endPage={endPage}
            totalRows={totalRows}
            setCurrentPage={setCurrentPage}
            setPerPage={setPerPage}
            onChangePage = {onChangePage}
            onRowsPerPageChange={onRowsPerPageChange}
          />
        }
      />
      <LoadingPage open={isEmailBehaviourLoading} />
      <SnackBar
              open={showSnackbar}
              severity={snackbarSeverity}
              message={snackbarMessage}
              onClose={() => setShowSnackbar(false)}
          />
    </Stack>
  );
};

const CellText = styled((props: TypographyProps) => (
  <Typography fontWeight={500} fontSize={14} {...props} />
))((props: TypographyProps) => ({
  sx: { fontWeight: props.fontWeight, fontSize: props.fontSize, textAlign:props.textAlign }
}));
